import jwt_decode from 'jwt-decode';
import isNull from 'lodash/isNull';

/**
 * Generates a script tag
 * @param {Object} arguments - arguments passed to the function
 * @param {HTMLElement} arguments.document - The HTML document.
 * @param {string} arguments.id - The id for the script tag.
 * @param {string} arguments.src - The source of the script.
 * @param {string} arguments.asyncStrategy - Allows the script to be loaded in different moments.
 *
 * @returns {HTMLElement} A script tag with the corresponding script source
 */
export const buildScriptTag = ({ doc, id, src, asyncStrategy }) => {
    const scriptTag = doc.createElement('script');
    scriptTag.id = id;
    scriptTag.type = 'text/javascript';
    scriptTag.src = src;

    if (asyncStrategy === 'defer') {
        scriptTag.defer = true;
    } else if (asyncStrategy === 'async') {
        scriptTag.async = true;
    }

    return scriptTag;
};

/**
 * Injects the script in the dom and executes the code when loaded.
 * @param {Object} arguments - arguments passed to the function
 * @param {HTMLElement} arguments.document - The HTML document.
 * @param {string} arguments.id - The id for the script tag.
 * @param {string} arguments.src - The source of the script.
 * @param {string} arguments.asyncStrategy - Allows the script to be loaded in different moments.
 * @param {function} arguments.onLoad - The function to execute after the script loads.
 */
export const injectScriptTag = ({ doc, id, src, asyncStrategy, onLoad }) => {
    if (isNull(doc.getElementById(id))) {
        const scriptTag = buildScriptTag({
            doc,
            id,
            src,
            asyncStrategy,
        });

        scriptTag.onload = () => {
            onLoad?.();
        };

        document.body.append(scriptTag);
    } else if (onLoad) {
        onLoad();
    }
};

/**
 *
 * @param {Object} response - The response from the apple service
 * @param {Object} response.authorization - Object containing the information for each user
 * @param {string} response.authorization.code - A single-use authentication code that is valid for five minutes.
 * @param {string} response.authorization.id_token - A JSON web token containing the user’s identify information.
 * @param {string} response.authorization.state - The state passed by the init function.
 * @param {string} response.authorization.user - A JSON string containing the data requested in the scope property. ONLY RETURNED THE FIRST TIME
 * * * { "name": { "firstName": string, "lastName": string }, "email": string }
     * * for further uses of this the id_token will have to be decoded
     *  {
            "authorization": {
                "state": "[STATE]",
                "code": "[CODE]",
                "id_token": "[ID_TOKEN]"
            },
            "user": {
                "email": "[EMAIL]",
                "name": {
                    "firstName": "[FIRST_NAME]",
                    "lastName": "[LAST_NAME]"
                }
            }
        }
 * @param {boolean} response.skipCreation - Allows to skip user account creation
 *
 * @returns {{
 *  email: string,
 *  expirationTimestamp: number,
 *  accessToken: string,
 *  skipCreation: boolean,
 *  isPrivateEmail: boolean,
 * }}
 */
export const buildAppleLoginResponse = (response = {}) => {
    const {
        // This is undefined most of the times.
        skipCreation,
        authorization: { id_token: idToken, code } = {},
        email: responseEmail,
    } = response;
    let decodedInfo = {};

    try {
        const { email, auth_time, sub, is_private_email } = jwt_decode(idToken);

        decodedInfo = {
            email: responseEmail || email,
            expirationTimestamp: auth_time,
            sub,
            isPrivateEmail: is_private_email === 'true',
        };
    } catch (error) {
        decodedInfo = {
            email: responseEmail || '',
            expirationTimestamp: '0',
            sub: '',
            isPrivateEmail: false,
        };
    }

    return {
        skipCreation,
        accessToken: code,
        ...decodedInfo,
    };
};
